import { Color, Spacing } from '@persona-id/design-tokens';
import { IdFrontIcon, MoreIcon, TrashIcon } from '@persona-id/design-tokens/icons';
import { isEmpty, union } from 'lodash/fp';
import { useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { FormError } from 'shared/components/FormControl';
import styled from 'styled-components';

import CopyableField from 'dashboard/components/CopyableField';
import HandlebarPill from 'dashboard/components/HandlebarFormControl/HandlebarPill';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import Permissioned from 'dashboard/components/Permissioned';
import ReactSelect, { ReactCreatableSelect } from 'dashboard/components/ReactSelect';
import { splitEmailAddresses } from 'dashboard/forms/FormSendCaseEmail/utils';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { parseHandlebarsText } from 'dashboard/lib/utils/handlebars';
import { CaseMessageRelatedObject } from 'dashboard/models/case-message';
import { CaseEmailTemplate } from 'dashboard/models/case-template';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { Template } from 'dashboard/models/template';
import { generateDefaultEmailBody, sanitizeHtmlEmail } from 'dashboard/modules/CaseEmail/helpers';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu/DropdownMenu';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Paragraph } from 'design-system/components/Text';
import TextArea from 'design-system/components/TextArea';
import inputMixin, { InputMixinProps } from 'design-system/mixins/input';
import { paragraphMixins } from 'design-system/mixins/text';
import { EMAIL_ADDRESS_REGEX } from 'lib/constants/regexes';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';
import { useFormSecurely } from 'lib/security/reactHookForm';
import { nameDisplay } from 'lib/utils/template';

const FROM_LABEL_WIDTH = 59;
const TO_LABEL_WIDTH = 39;
const SUBJECT_LABEL_WIDTH = 76;

const CASE_VARIABLES = ['case.id'];

const StyledButton = styled(Button)`
  padding: 3px 6px;
  height: auto;
  width: fit-content;
`;

const LabelInputContainer = styled.div`
  position: relative;
`;

const ToInputContainer = styled.div`
  position: relative;
  z-index: 2;
`;

const StyledLabel = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  z-index: 1;
  color: ${Color.CoreSlate1000};
  ${paragraphMixins.mdBold}
  padding: 7px 12px;
`;

const StyledInput = styled(Input)<{ paddingLeft: number }>`
  ${({ paddingLeft }) => `padding-left: ${paddingLeft}px;`}
  transition: padding-left 0;
`;

const PillsTextAreaWrapper = styled.div<InputMixinProps & { visible: boolean }>`
  ${inputMixin};
  position: relative;
  white-space: pre-wrap;
  word-break: break-word;
  pointer-events: none;
  padding-bottom: 0;
  height: 150px;
  visibility: ${({ visible }) => (visible ? 'initial' : 'hidden')};
  overflow: auto;
`;

const StyledTextArea = styled(TextArea)<{ visible: boolean }>`
  position: absolute;
  visibility: ${({ visible }) => (visible ? 'initial' : 'hidden')};
  height: 150px;
`;

const EmailBodyContainer = styled.div`
  position: relative;
`;

export interface FormSendCaseEmailProps {
  defaultValues: FormSendCaseEmailData;
  emailAddresses: string[];
  inquiryTemplates: (InquiryTemplate | Template)[];
  caseEmailTemplates: CaseEmailTemplate[];
  defaultCaseObjectRefId: string;
  handleDelete: () => void;
  sendEmail: (data: FormSendCaseEmailData) => Promise<void>;
  handleUpdateEmailDraft: (values: FormSendCaseEmailData) => Promise<void>;
  showFromField?: boolean;
}

export const FormSendCaseEmail = ({
  defaultValues,
  emailAddresses,
  inquiryTemplates,
  caseEmailTemplates,
  defaultCaseObjectRefId,
  handleDelete,
  sendEmail,
  handleUpdateEmailDraft,
  showFromField = true,
}: FormSendCaseEmailProps) => {
  const {
    register,
    getValues,
    setValue,
    handleSubmit,
    watch,
    reset,
    control,
    formState: { isDirty, isSubmitting, errors, isValid },
  } = useFormSecurely<FormSendCaseEmailData>({
    defaultValues,
    mode: 'onChange',
  });

  // Refreshes the email draft form when the user clicks on another email
  useEffect(() => {
    reset({ ...defaultValues });
  }, [defaultValues['last-updated-at']]);

  const [createInquiryModal, { enable: showCreateInquiryModal, disable: hideCreateInquiryModal }] =
    useToggle(false);
  const [focusEmailBody, { enable: onFocusEmailBody, disable: onBlurEmailBody }] = useToggle(false);
  const showEmailBodyPills = !focusEmailBody && getValues('body-text').length > 0;
  const attachedInquiries = watch('related-objects');
  const selectedInquiryTemplateId =
    attachedInquiries.length > 0 ? attachedInquiries[0].data['inquiry-template-id'] : '';
  const referenceId =
    attachedInquiries.length > 0
      ? attachedInquiries[0].data['reference-id']
      : defaultCaseObjectRefId;

  const personaDomain = window.location.hostname.includes('withpersona-staging')
    ? '@cases.withpersona-staging.com'
    : '@cases.withpersona.com';

  const emailTemplatesDropdownList = caseEmailTemplates.map((emailTemplate) => {
    return {
      id: emailTemplate.token,
      label: emailTemplate.name,
      variant: 'basic' as const,
      onClick: () => {
        setValue('subject', emailTemplate.subject, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setValue(
          'body-text',
          `${emailTemplate.body}${generateDefaultEmailBody(defaultValues['from-email-address'])}`,
          {
            shouldDirty: true,
            shouldValidate: true,
          },
        );
        setValue('cc-email-addresses', emailTemplate.cc, {
          shouldDirty: true,
          shouldValidate: true,
        });
      },
    };
  });

  const pills = useMemo(() => {
    const rawEmailBody = getValues('body-text');
    if (focusEmailBody) {
      return rawEmailBody;
    }
    try {
      return parseHandlebarsText(rawEmailBody).map(
        ({ content, variableName: word, position }, index) => {
          if (word == null) {
            return content;
          } else {
            const valid =
              getValues('related-objects')
                .map((att) => att.name)
                .includes(word) || CASE_VARIABLES.includes(word);
            const onDelete = () => {
              const valueWithoutHandlebarWord =
                rawEmailBody.slice(0, position.start) + rawEmailBody.slice(position.end + 1);
              setValue('body-text', valueWithoutHandlebarWord, {
                shouldDirty: true,
                shouldValidate: true,
              });
            };

            return <HandlebarPill key={index} value={word} error={!valid} onDelete={onDelete} />;
          }
        },
      );
    } catch {
      return rawEmailBody;
    }
  }, [onFocusEmailBody, attachedInquiries, getValues('body-text'), setValue]);

  const onSelectInquiryTemplate = (inquiryTemplateId: string) => {
    setValue(
      'related-objects',
      [
        {
          name: 'inquiry',
          type: 'inquiry',
          data: {
            'inquiry-template-id': inquiryTemplateId,
            'reference-id': referenceId,
          },
        },
      ],
      { shouldDirty: true, shouldValidate: true },
    );
  };

  const onUpdateReferenceId = (referenceId: string) => {
    setValue(
      'related-objects',
      [
        {
          name: 'inquiry',
          type: 'inquiry',
          data: {
            'inquiry-template-id': selectedInquiryTemplateId,
            'reference-id': referenceId,
          },
        },
      ],
      { shouldDirty: true, shouldValidate: true },
    );
  };

  const clearInquiryTemplate = () => {
    setValue('related-objects', [], { shouldDirty: true, shouldValidate: true });
  };

  const validateEmailBody = (emailBody: string) => {
    try {
      return parseHandlebarsText(emailBody).every(({ variableName }) => {
        if (variableName == null) {
          return true;
        } else {
          return (
            getValues('related-objects')
              .map((att) => att.name)
              .includes(variableName) || CASE_VARIABLES.includes(variableName)
          );
        }
      });
    } catch {
      return true;
    }
  };

  const onSendEmail = (data: FormSendCaseEmailData) => {
    const values = {
      ...data,
      'body-text': sanitizeHtmlEmail(data['body-text']),
    };
    sendEmail(values);
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSendEmail)}>
        {showFromField && (
          <>
            <LabelInputContainer>
              <StyledLabel>From</StyledLabel>
              <StyledInput
                {...register('from-email-address', {
                  required: 'Please configure a default from email address in the case template.',
                })}
                id='form-send-case-email__from_email_address'
                disabled={true}
                paddingLeft={FROM_LABEL_WIDTH}
                role='input'
                aria-label='From email addresses'
              />
            </LabelInputContainer>
            {!defaultValues['from-email-address'] && (
              <FormError>
                Please configure a default from email address in the case template.
              </FormError>
            )}
            <Spacer height={1} />
          </>
        )}
        <Controller
          control={control}
          name='to-email-addresses'
          rules={{
            required: 'Please enter an email address',
            validate: (values: string[]) =>
              values.every(
                (value) =>
                  EMAIL_ADDRESS_REGEX.test(value) && !value.toLowerCase().endsWith(personaDomain),
              ) || 'The email you entered is not valid.',
          }}
          render={({ field: { onChange, value } }) => (
            <ToInputContainer>
              <StyledLabel>To</StyledLabel>
              <ReactCreatableSelect
                id='form-send-case-email__to_email_addresses'
                value={value}
                options={union(value, emailAddresses).map((email: string) => ({
                  label: email,
                  value: email,
                }))}
                onChange={(values: string[]) => {
                  onChange(splitEmailAddresses(values));
                }}
                placeholder='recipient@withpersona.com'
                hasError={!isEmpty(errors['to-email-addresses'])}
                portal={true}
                isMulti={true}
                isClearable={true}
                styles={{
                  valueContainer: () => ({
                    paddingLeft: TO_LABEL_WIDTH,
                    zIndex: 2,
                  }),
                }}
                aria-label='To email addresses'
              />
            </ToInputContainer>
          )}
        />
        {errors['to-email-addresses'] && (
          <FormError>{(errors['to-email-addresses'] as any)?.message}</FormError>
        )}
        <Spacer height={1} />
        <Controller
          control={control}
          name='cc-email-addresses'
          rules={{
            validate: (values: string[]) =>
              values.every(
                (value) =>
                  EMAIL_ADDRESS_REGEX.test(value) && !value.toLowerCase().endsWith(personaDomain),
              ) || 'The email you entered is not valid.',
          }}
          render={({ field: { onChange, value } }) => (
            <ToInputContainer>
              <StyledLabel>Cc</StyledLabel>
              <ReactCreatableSelect
                id='form-send-case-email__cc-email-addresses'
                value={value}
                options={value.map((email: string) => ({
                  label: email,
                  value: email,
                }))}
                onChange={(values: string[]) => {
                  onChange(splitEmailAddresses(values));
                }}
                hasError={!isEmpty(errors['cc-email-addresses'])}
                placeholder='cc-recipient@withpersona.com'
                portal={true}
                isMulti={true}
                isClearable={true}
                styles={{
                  valueContainer: () => ({
                    paddingLeft: TO_LABEL_WIDTH,
                    zIndex: 2,
                  }),
                }}
                aria-label='Cc email addresses'
              />
            </ToInputContainer>
          )}
        />
        {errors['cc-email-addresses'] && (
          <FormError>{(errors['cc-email-addresses'] as any)?.message}</FormError>
        )}
        <Spacer height={1} />
        <LabelInputContainer>
          <StyledLabel>Subject</StyledLabel>
          <StyledInput
            {...register('subject', {
              required: 'Please enter a subject',
            })}
            id='form-send-case-email__subject'
            placeholder='Subject'
            disabled={isSubmitting}
            paddingLeft={SUBJECT_LABEL_WIDTH}
            role='input'
          />
        </LabelInputContainer>
        {errors.subject && <FormError>{errors.subject.message}</FormError>}
        <Spacer height={1} />
        <EmailBodyContainer
          id='form-send-case-email__email-body-container'
          tabIndex={0}
          onFocus={onFocusEmailBody}
          onClick={onFocusEmailBody}
          onBlur={onBlurEmailBody}
        >
          <Controller
            control={control}
            name='body-text'
            rules={{ required: 'Please enter an email body', validate: validateEmailBody }}
            render={({ field: { onChange, value } }) => (
              <StyledTextArea
                onChange={onChange}
                value={value}
                id='form-send-case-email__body-text'
                aria-label='Email body'
                disabled={isSubmitting}
                placeholder='Compose your message here...'
                visible={!showEmailBodyPills}
              />
            )}
          />

          <PillsTextAreaWrapper visible={showEmailBodyPills}>{pills}</PillsTextAreaWrapper>
        </EmailBodyContainer>
        {errors['body-text'] && <FormError>{errors['body-text'].message}</FormError>}
        <Spacer height={2} />
        <HStack justify='space-between'>
          <HStack equal spacing={Spacing[1]}>
            <Button
              id='form-send-case-email__discard'
              variant='inverse'
              width='icon'
              onClick={handleDelete}
              aria-label='Delete'
              disabled={isSubmitting}
            >
              <TrashIcon />
            </Button>
            <Permissioned permission={Permission.InquiryWrite}>
              <Button
                id='form-send-case-email__attach-inquiry'
                variant='inverse'
                width='icon'
                onClick={showCreateInquiryModal}
                aria-label='Create'
                disabled={inquiryTemplates.length === 0 || isSubmitting}
              >
                <IdFrontIcon />
              </Button>
            </Permissioned>
            <DropdownMenu
              trigger={
                <Button
                  id='form-send-case-email__email_templates_dropdown'
                  variant='inverse'
                  width='icon'
                  aria-label='Load email template'
                >
                  <MoreIcon />
                </Button>
              }
              align='center'
            >
              {emailTemplatesDropdownList.map((item) => (
                <DropdownMenu.Item
                  id={item.id}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    item.onClick();
                  }}
                  key={item.id}
                  variant={item.variant}
                >
                  {item.label}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu>
          </HStack>
          <HStack equal spacing={Spacing[2]}>
            <StyledButton
              id='form-send-case-email__save'
              variant='basic'
              disabled={isSubmitting || !isDirty}
              isPending={isSubmitting}
              size='sm'
              name='save-email'
              onClick={() => handleUpdateEmailDraft(getValues())}
            >
              Save
            </StyledButton>
            <StyledButton
              variant='primary'
              type='submit'
              disabled={isSubmitting || !isValid}
              isPending={isSubmitting}
              size='sm'
              name='send-email'
            >
              Send
            </StyledButton>
          </HStack>
        </HStack>
      </form>
      <ModalWrapper isOpen={createInquiryModal} handleCloseModal={hideCreateInquiryModal}>
        <ModalContainer>
          <ModalHeader onClose={hideCreateInquiryModal}>Select inquiry template</ModalHeader>
          <ModalBody>
            <CreateInquiryModal
              selectedInquiryTemplateId={selectedInquiryTemplateId}
              referenceId={referenceId}
              inquiryTemplates={inquiryTemplates}
              onSelectInquiryTemplate={onSelectInquiryTemplate}
              onUpdateReferenceId={onUpdateReferenceId}
              clearSelectedInquiryTemplate={clearInquiryTemplate}
            />
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>
    </>
  );
};

interface CreateInquiryModalProps {
  selectedInquiryTemplateId: string;
  referenceId: string;
  inquiryTemplates: (InquiryTemplate | Template)[];
  onSelectInquiryTemplate: (inquiryTemplateId: string) => void;
  onUpdateReferenceId: (referenceId: string) => void;
  clearSelectedInquiryTemplate: () => void;
}
const CreateInquiryModal = ({
  selectedInquiryTemplateId,
  referenceId,
  inquiryTemplates,
  onSelectInquiryTemplate,
  onUpdateReferenceId,
  clearSelectedInquiryTemplate,
}: CreateInquiryModalProps) => {
  return (
    <>
      {selectedInquiryTemplateId && (
        <>
          <Paragraph>
            Copy the inquiry link placeholder to your clipboard and paste it anywhere in the email
            body. The placeholder will become an inquiry link once the email is sent.
          </Paragraph>
          <Spacer height={1} />
        </>
      )}
      <ReactSelect
        value={selectedInquiryTemplateId}
        options={inquiryTemplates
          .map((inquiryTemplate) => {
            return {
              label: nameDisplay(inquiryTemplate),
              value: inquiryTemplate.id,
            };
          })
          .sort((a, b) => a.label.localeCompare(b.label))}
        onChange={onSelectInquiryTemplate}
        portal={true}
      />
      {selectedInquiryTemplateId && (
        <>
          <Spacer height={1} />
          <CopyableField value='{{inquiry}}' />
          <Spacer height={1} />
          <Input
            placeholder='Reference ID (optional)'
            onChange={(event) => onUpdateReferenceId(event.target.value)}
            value={referenceId}
          />
        </>
      )}
      <Spacer height={2} />
      {selectedInquiryTemplateId && (
        <Button
          id='form-send-email-create-inquiry-modal__delete'
          onClick={clearSelectedInquiryTemplate}
          variant='danger'
          width='auto'
        >
          Delete
        </Button>
      )}
    </>
  );
};

export interface FormSendCaseEmailData {
  id: string;
  subject: string;
  'from-email-address': string;
  'to-email-addresses': string[];
  'cc-email-addresses': string[];
  'body-text': string;
  'last-updated-at': string;
  'related-objects': CaseMessageRelatedObject[];
}
